import ImagequantModuleFactory from "./wasm-vendor/imagequant.js";
import imagequantWasmUrl from "./wasm-vendor/imagequant.wasm" with { type: "file" };

// The @squoosh-kit npm wrappers load these codecs via dynamic import() with
// relative candidate paths designed for generic bundlers — Bun's bundler
// doesn't reliably resolve/emit those at runtime (404s in the browser).
// Loading the vendored glue + wasm binary directly, with
// `with { type: "file" }` for the wasm asset, is the pattern Bun's bundler
// actually supports.

interface ImagequantModule {
  quantize(
    data: Uint8Array,
    width: number,
    height: number,
    numColors: number,
    dither: number
  ): Uint8Array;
}

let imagequantModulePromise: Promise<ImagequantModule> | null = null;
function loadImagequantModule(): Promise<ImagequantModule> {
  if (!imagequantModulePromise) {
    imagequantModulePromise = (async () => {
      const wasmBinary = await fetch(imagequantWasmUrl).then((r) => r.arrayBuffer());
      return ImagequantModuleFactory({ noInitialRun: true, wasmBinary }) as Promise<ImagequantModule>;
    })();
  }
  return imagequantModulePromise;
}

export async function quantizeImage(
  data: Uint8ClampedArray | Uint8Array,
  width: number,
  height: number,
  numColors: number,
  dither: number
): Promise<{ data: Uint8Array; width: number; height: number }> {
  const module = await loadImagequantModule();
  const input = data instanceof Uint8Array ? data : new Uint8Array(data.buffer, data.byteOffset, data.length);
  const result = module.quantize(input, width, height, numColors, dither);
  if (!result) {
    throw new Error("PNG quantization failed");
  }
  return { data: result, width, height };
}

// A palette (PNG color type 3) PNG stores one byte per pixel plus a lookup
// table, instead of 4 bytes per pixel — that's the actual size win real PNG
// quantizer tools (pngquant etc.) get. ImageQuant's wasm binding only
// exposes a "remap to RGBA" function, not raw indices, so the palette here
// is rebuilt by scanning the already-quantized RGBA output: after
// quantizeImage() there are at most `numColors` distinct RGBA values, so
// this is cheap and exact.
const PNG_SIGNATURE = new Uint8Array([137, 80, 78, 71, 13, 10, 26, 10]);

const CRC_TABLE = (() => {
  const table = new Uint32Array(256);
  for (let n = 0; n < 256; n++) {
    let c = n;
    for (let k = 0; k < 8; k++) {
      c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
    }
    table[n] = c >>> 0;
  }
  return table;
})();

function crc32(bytes: Uint8Array): number {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i++) {
    crc = CRC_TABLE[(crc ^ bytes[i]) & 0xff] ^ (crc >>> 8);
  }
  return (crc ^ 0xffffffff) >>> 0;
}

function makeChunk(type: string, data: Uint8Array): Uint8Array {
  const typeBytes = new Uint8Array(4);
  for (let i = 0; i < 4; i++) typeBytes[i] = type.charCodeAt(i);
  const chunk = new Uint8Array(4 + 4 + data.length + 4);
  const view = new DataView(chunk.buffer);
  view.setUint32(0, data.length);
  chunk.set(typeBytes, 4);
  chunk.set(data, 8);
  const crcInput = new Uint8Array(4 + data.length);
  crcInput.set(typeBytes, 0);
  crcInput.set(data, 4);
  view.setUint32(8 + data.length, crc32(crcInput));
  return chunk;
}

async function deflateZlib(data: Uint8Array): Promise<Uint8Array> {
  const stream = new CompressionStream("deflate");
  const writer = stream.writable.getWriter();
  writer.write(data.buffer as ArrayBuffer);
  writer.close();
  const chunks: Uint8Array[] = [];
  const reader = stream.readable.getReader();
  for (;;) {
    const { done, value } = await reader.read();
    if (done) break;
    chunks.push(value);
  }
  const total = chunks.reduce((sum, c) => sum + c.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const c of chunks) {
    out.set(c, offset);
    offset += c.length;
  }
  return out;
}

function concat(arrays: Uint8Array[]): Uint8Array {
  const total = arrays.reduce((sum, a) => sum + a.length, 0);
  const out = new Uint8Array(total);
  let offset = 0;
  for (const a of arrays) {
    out.set(a, offset);
    offset += a.length;
  }
  return out;
}

export async function encodeIndexedPng(
  rgba: Uint8Array,
  width: number,
  height: number
): Promise<Uint8Array> {
  const pixelCount = width * height;
  const colorToIndex = new Map<number, number>();
  const palette: number[] = [];
  const indices = new Uint8Array(pixelCount);

  for (let i = 0; i < pixelCount; i++) {
    const o = i * 4;
    const key = (rgba[o] | (rgba[o + 1] << 8) | (rgba[o + 2] << 16) | (rgba[o + 3] << 24)) >>> 0;
    let idx = colorToIndex.get(key);
    if (idx === undefined) {
      if (palette.length >= 256) {
        throw new Error("Image has more than 256 distinct colors after quantization");
      }
      idx = palette.length;
      palette.push(key);
      colorToIndex.set(key, idx);
    }
    indices[i] = idx;
  }

  const hasAlpha = palette.some((c) => ((c >>> 24) & 0xff) !== 255);

  const stride = width + 1;
  const raw = new Uint8Array(stride * height);
  for (let y = 0; y < height; y++) {
    raw[y * stride] = 0; // filter type: None
    raw.set(indices.subarray(y * width, (y + 1) * width), y * stride + 1);
  }
  const idatData = await deflateZlib(raw);

  const ihdr = new Uint8Array(13);
  const ihdrView = new DataView(ihdr.buffer);
  ihdrView.setUint32(0, width);
  ihdrView.setUint32(4, height);
  ihdr[8] = 8; // bit depth
  ihdr[9] = 3; // color type: indexed
  ihdr[10] = 0; // compression
  ihdr[11] = 0; // filter
  ihdr[12] = 0; // interlace

  const plte = new Uint8Array(palette.length * 3);
  for (let i = 0; i < palette.length; i++) {
    const c = palette[i];
    plte[i * 3] = c & 0xff;
    plte[i * 3 + 1] = (c >>> 8) & 0xff;
    plte[i * 3 + 2] = (c >>> 16) & 0xff;
  }

  const parts = [PNG_SIGNATURE, makeChunk("IHDR", ihdr), makeChunk("PLTE", plte)];
  if (hasAlpha) {
    const trns = new Uint8Array(palette.length);
    for (let i = 0; i < palette.length; i++) trns[i] = (palette[i] >>> 24) & 0xff;
    parts.push(makeChunk("tRNS", trns));
  }
  parts.push(makeChunk("IDAT", idatData));
  parts.push(makeChunk("IEND", new Uint8Array(0)));

  return concat(parts);
}
